/**
 * Google Tag Manager Service
 * Provides comprehensive tracking for all business-critical events
 */

import type {
  GTMEvent,
  GTMUserType,
  UserSignupEvent,
  UserLoginEvent,
  UserLogoutEvent,
  OrderCreatedEvent,
  PaymentAuthorizedEvent,
  PaymentCompletedEvent,
  CheckoutStartedEvent,
  ServiceCategoryViewEvent,
  WorkerProfileViewEvent,
  FormSubmitEvent,
  ButtonClickEvent,
  SearchEvent,
  AddToFavoritesEvent,
  ChatStartedEvent,
} from '../types/gtm';
import { posthogService } from './posthogService';

class GTMService {
  private initialized = false;
  private gtmId: string | null = null;

  /**
   * Initialize GTM with container ID
   */
  public initialize(gtmId: string | null): void {
    if (!gtmId || this.initialized) {
      return;
    }

    this.gtmId = gtmId;
    this.initialized = true;

    // Initialize dataLayer if it doesn't exist
    // Note: The GTM snippet already initializes dataLayer and pushes gtm.js event,
    // so we don't need to do it here
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  }

  /**
   * Check if GTM is initialized and ready
   */
  public isInitialized(): boolean {
    return this.initialized && this.gtmId !== null;
  }

  /**
   * Push event to dataLayer
   */
  public push(event: GTMEvent | Record<string, unknown>): void {
    if (typeof window === 'undefined') {
      return;
    }

    posthogService.forwardGtmEvent(event);

    if (!this.isInitialized()) {
      if (process.env.NODE_ENV === 'development' && !posthogService.isReady()) {
        console.log('[GTM] Event not sent (GTM not initialized):', event);
      }
      return;
    }

    try {
      window.dataLayer.push(event);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[GTM] Event sent:', event);
      }
    } catch (error) {
      console.error('[GTM] Error pushing event:', error);
    }
  }

  /**
   * Track page view
   */
  public trackPageView(pageTitle?: string, pagePath?: string): void {
    this.push({
      event: 'page_view',
      page_title: pageTitle || document.title,
      page_location: window.location.href,
      page_path: pagePath || window.location.pathname,
    });
  }

  /**
   * Track user signup
   */
  public trackUserSignup(
    userType: GTMUserType,
    userId?: string | number,
    method?: 'email' | 'phone'
  ): void {
    const event: UserSignupEvent = {
      event: 'user_signup',
      user_type: userType,
      method,
    };

    if (userId) {
      event.user_id = userId;
    }

    this.push(event);
  }

  /**
   * Track user login
   */
  public trackUserLogin(
    userType: GTMUserType,
    userId?: string | number,
    method?: 'email' | 'phone'
  ): void {
    const event: UserLoginEvent = {
      event: 'user_login',
      user_type: userType,
      method,
    };

    if (userId) {
      event.user_id = userId;
    }

    this.push(event);
  }

  /**
   * Track user logout
   */
  public trackUserLogout(userType?: GTMUserType, userId?: string | number): void {
    const event: UserLogoutEvent = {
      event: 'user_logout',
    };

    if (userType) {
      event.user_type = userType;
    }

    if (userId) {
      event.user_id = userId;
    }

    this.push(event);
  }

  /**
   * Track order creation
   */
  public trackOrderCreated(params: {
    orderId: string | number;
    orderValue: number;
    currency?: string;
    serviceCategory?: string;
    serviceSubcategory?: string;
    customerId?: string | number;
    preferredWorkerId?: string | number;
  }): void {
    const event: OrderCreatedEvent = {
      event: 'order_created',
      order_id: params.orderId,
      order_value: params.orderValue,
      currency: params.currency || 'CAD',
      customer_id: params.customerId,
      preferred_worker_id: params.preferredWorkerId,
    };

    if (params.serviceCategory) {
      event.service_category = params.serviceCategory;
    }

    if (params.serviceSubcategory) {
      event.service_subcategory = params.serviceSubcategory;
    }

    this.push(event);
  }

  /**
   * Track payment authorization
   */
  public trackPaymentAuthorized(params: {
    orderId: string | number;
    value: number;
    currency?: string;
    transactionId?: string;
    paymentMethod?: string;
    customerId?: string | number;
  }): void {
    const event: PaymentAuthorizedEvent = {
      event: 'payment_authorized',
      order_id: params.orderId,
      value: params.value,
      currency: params.currency || 'CAD',
      transaction_id: params.transactionId,
      payment_method: params.paymentMethod,
      customer_id: params.customerId,
    };

    this.push(event);
  }

  /**
   * Track payment completion
   */
  public trackPaymentCompleted(params: {
    orderId: string | number;
    value: number;
    currency?: string;
    transactionId?: string;
    paymentMethod?: string;
    customerId?: string | number;
  }): void {
    const event: PaymentCompletedEvent = {
      event: 'payment_completed',
      order_id: params.orderId,
      value: params.value,
      currency: params.currency || 'CAD',
      transaction_id: params.transactionId,
      payment_method: params.paymentMethod,
      customer_id: params.customerId,
    };

    this.push(event);
  }

  /**
   * Track checkout started
   */
  public trackCheckoutStarted(params: {
    orderId: string | number;
    value: number;
    currency?: string;
    customerId?: string | number;
  }): void {
    const event: CheckoutStartedEvent = {
      event: 'checkout_started',
      order_id: params.orderId,
      value: params.value,
      currency: params.currency || 'CAD',
      customer_id: params.customerId,
    };

    this.push(event);
  }

  /**
   * Track service category view
   */
  public trackServiceCategoryView(categoryName: string, categorySlug?: string): void {
    const event: ServiceCategoryViewEvent = {
      event: 'service_category_view',
      category_name: categoryName,
      category_slug: categorySlug,
    };

    this.push(event);
  }

  /**
   * Track worker profile view
   */
  public trackWorkerProfileView(
    workerId: string | number,
    workerName?: string,
    source?: 'search' | 'favorites' | 'direct' | 'recommendation'
  ): void {
    const event: WorkerProfileViewEvent = {
      event: 'worker_profile_view',
      worker_id: workerId,
      worker_name: workerName,
      source,
    };

    this.push(event);
  }

  /**
   * Track form submission
   */
  public trackFormSubmit(
    formName: string,
    formLocation?: string,
    success?: boolean
  ): void {
    const event: FormSubmitEvent = {
      event: 'form_submit',
      form_name: formName,
      form_location: formLocation || window.location.pathname,
      success,
    };

    this.push(event);
  }

  /**
   * Track button click
   */
  public trackButtonClick(
    buttonName: string,
    buttonLocation?: string,
    buttonType?: string
  ): void {
    const event: ButtonClickEvent = {
      event: 'button_click',
      button_name: buttonName,
      button_location: buttonLocation || window.location.pathname,
      button_type: buttonType,
    };

    this.push(event);
  }

  /**
   * Track search
   */
  public trackSearch(
    searchTerm: string,
    searchType?: 'service' | 'worker' | 'general',
    resultsCount?: number
  ): void {
    const event: SearchEvent = {
      event: 'search',
      search_term: searchTerm,
      search_type: searchType,
      results_count: resultsCount,
    };

    this.push(event);
  }

  /**
   * Track add to favorites
   */
  public trackAddToFavorites(
    itemType: 'worker' | 'service',
    itemId: string | number,
    userId?: string | number
  ): void {
    const event: AddToFavoritesEvent = {
      event: 'add_to_favorites',
      item_type: itemType,
      item_id: itemId,
      user_id: userId,
    };

    this.push(event);
  }

  /**
   * Track chat started
   */
  public trackChatStarted(
    chatId: string | number,
    orderId?: string | number,
    participantType?: 'customer' | 'worker'
  ): void {
    const event: ChatStartedEvent = {
      event: 'chat_started',
      chat_id: chatId,
      order_id: orderId,
      participant_type: participantType,
    };

    this.push(event);
  }

  /**
   * Set user properties (for all subsequent events)
   */
  public setUserProperties(params: {
    userId?: string | number;
    userType?: GTMUserType;
    email?: string;
    [key: string]: string | number | GTMUserType | null | undefined;
  }): void {
    this.push({
      event: 'set_user_properties',
      ...params,
    });
  }

  /**
   * Clear user properties
   */
  public clearUserProperties(): void {
    this.push({
      event: 'clear_user_properties',
    });
  }
}

// Export singleton instance
export const gtmService = new GTMService();
